import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import datos from "../assets/json/datos.json";
import fondoDefault from "../assets/img/fondoOferta.webp";

export default function Oferta() {
  const navigate = useNavigate();
  const { sede } = useParams();

  const [carreras, setCarreras] = useState([]);
  const [fondo, setFondo] = useState(fondoDefault);

  const containerRef = useRef(null);
  const [itemsPorFila, setItemsPorFila] = useState(4);

  // ===============================
  // CALCULAR ITEMS POR FILA (REACTIVO REAL)
  // ===============================
  useEffect(() => {
    const calcular = () => {
      if (!containerRef.current) return;

      const ancho = containerRef.current.offsetWidth;

      const itemWidth = 210; // ancho real aproximado de cada card
      const gap = 27; // 1.7rem aprox

      const total = Math.floor(ancho / (itemWidth + gap));

      setItemsPorFila(Math.max(1, total));
    };

    calcular();
    window.addEventListener("resize", calcular);
    return () => window.removeEventListener("resize", calcular);
  }, []);

  // ===============================
  // DISTRIBUCIÓN SIMÉTRICA
  // ===============================
  const dividirEnFilas = (lista, maxPorFila) => {
    const n = lista.length;

    let filas = Math.round(n / maxPorFila);
    if (n > maxPorFila) filas = Math.max(2, filas);
    if (filas < 1) filas = 1;

    const base = Math.floor(n / filas);
    const extra = n % filas;

    const resultado = [];
    let index = 0;

    for (let i = 0; i < filas; i++) {
      const cantidad = base + (i < extra ? 1 : 0);
      resultado.push(lista.slice(index, index + cantidad));
      index += cantidad;
    }

    return resultado;
  };

  const filas = dividirEnFilas(carreras, itemsPorFila);

  // ===============================
  // IMÁGENES
  // ===============================
  const getImagenCarrera = (nombre) => {
    try {
      return require(`../assets/img/carreras/${nombre}`);
    } catch {
      return "";
    }
  };

  const getImagenFondo = (nombre) => {
    try {
      return require(`../assets/img/${nombre}`);
    } catch {
      return fondoDefault;
    }
  };

  const normalize = (str) => {
    return str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
  };

  // ===============================
  // CARGAR DATOS
  // ===============================
  useEffect(() => {
    const sedeKey = Object.keys(datos).find(
      (s) => normalize(s) === normalize(sede || "")
    );

    if (sedeKey && datos[sedeKey]) {
      const data = datos[sedeKey];

      if (data.oferta) {
        const lista = data.oferta.map((c) => ({
          title: c.title,
          img: getImagenCarrera(c.imagen),
        }));
        setCarreras(lista);
      } else {
        setCarreras([]);
      }

      if (data.imagenes?.fondoOferta) {
        setFondo(getImagenFondo(data.imagenes.fondoOferta));
      } else {
        setFondo(fondoDefault);
      }
    }
  }, [sede]);

  return (
    <section
      id="oferta"
      className="py-5"
      style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.70), rgba(255,255,255,0.70)), url(${fondo})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100%",
      }}
    >
      <div className="container text-center">

        {/* TITULO */}
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="fw-bold mb-3"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.3rem)",
            color: "#002166",
          }}
        >
          Oferta académica
        </motion.h2>

        {/* TEXTO */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 1 }}
          className="mb-5"
          style={{
            fontSize: "clamp(1.1rem, 2.5vw, 1.6rem)",
            color: "#002166",
          }}
        >
          Contamos con carreras con mayor demanda laboral.
        </motion.p>

        {/* GRID */}
        <div
          ref={containerRef}
          style={{
            maxWidth: "980px",
            margin: "0 auto",
          }}
        >
          {filas.map((fila, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "1.7rem",
                marginBottom: "1.7rem",
              }}
            >
              {fila.map((carrera, i) => (
                <motion.div
                  key={i}
                  className="text-center"
                  style={{
                    width: "200px",
                  }}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <img
                    src={carrera.img}
                    alt={carrera.title}
                    className="img-fluid rounded shadow"
                    style={{
                      maxHeight: "190px",
                      objectFit: "contain",
                      padding: "5px",
                      borderRadius: "12px",
                    }}
                  />

                  <motion.span
                    className="d-block fw-bold py-2 rounded-pill mt-3"
                    style={{
                      background: "#002166",
                      color: "white",
                      width: "100%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "pointer",
                    }}
                    whileHover={{
                      scale: 1.07,
                      backgroundColor: "#003399",
                    }}
                    onClick={() =>
                      navigate(
                        `/${sede}/Carrera/${normalize(carrera.title)}`
                      )
                    }
                  >
                    {carrera.title}
                  </motion.span>
                </motion.div>
              ))}
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}